import { FlakinessReport } from "@flakiness/flakiness-report";
import { WireTypes } from "@flakiness/server/common/wireTypes.js";

/**
 * Everything about the forge a project's source lives on: links into it, the
 * naming that goes next to them, and parsers for the URLs users paste in.
 */
export namespace forgeUtils {
  export function isGithub(project?: WireTypes.Project): boolean {
    return project?.sourceType === 'github_app' || project?.sourceType === 'github_pat';
  }

  export function isGitlab(project?: WireTypes.Project): boolean {
    return project?.sourceType === 'gitlab_pat';
  }

  /**
   * Local checkouts and JSON repos have no forge behind them, so there is
   * nothing to link to.
   */
  export function hasSourceUrl(project?: WireTypes.Project): project is WireTypes.Project {
    if (!project)
      return false;
    return isGithub(project) || isGitlab(project);
  }

  export function providerName(project?: WireTypes.Project): string {
    return isGitlab(project) ? 'GitLab' : 'GitHub';
  }

  export function providerIcon(project?: WireTypes.Project): string {
    return isGitlab(project) ? 'gitlab' : 'github';
  }

  export function pullRequestLabel(project?: WireTypes.Project): string {
    return isGitlab(project) ? 'Merge Request' : 'Pull Request';
  }

  export function pullRequestLabelPlural(project?: WireTypes.Project): string {
    return isGitlab(project) ? 'Merge Requests' : 'Pull Requests';
  }

  export function projectUrl(project: WireTypes.Project) {
    const host = isGitlab(project) ? (project.sourceOrigin ?? 'https://gitlab.com') : 'https://github.com';
    return `${host}/${project.sourceOwner}/${project.sourceRepo}`;
  }

  export function fileUrl(project: WireTypes.Project, commitId: FlakinessReport.CommitId, gitFilePath: FlakinessReport.GitFilePath, lineNumber?: FlakinessReport.Number1Based) {
    // GitLab anchors file lines as #L12, same as GitHub, but routes blobs
    // through its `/-/` namespace and has no `?plain=1` equivalent.
    if (isGitlab(project))
      return `${projectUrl(project)}/-/blob/${commitId}/${gitFilePath}${lineNumber ? '#L' + lineNumber : ''}`;
    return `${projectUrl(project)}/blob/${commitId}/${gitFilePath}?plain=1${lineNumber ? '#L' + lineNumber : ''}`;
  }

  export function testUrl(project: WireTypes.Project, testStats: WireTypes.TestStats) {
    return testStats.test.filePath ? fileUrl(project, testStats.commitId, testStats.test.filePath, testStats.lineNumber) : undefined;
  }

  export function pullRequestUrl(project: WireTypes.Project, pr: string) {
    if (isGitlab(project))
      return `${projectUrl(project)}/-/merge_requests/${pr}`;
    return `${projectUrl(project)}/pull/${pr}`;
  }

  export function commitUrl(project: WireTypes.Project, commitId: FlakinessReport.CommitId) {
    if (isGitlab(project))
      return `${projectUrl(project)}/-/commit/${commitId}`;
    return `${projectUrl(project)}/commit/${commitId}`;
  }

  /**
   * A 5xx from the forge means our data is stale rather than misconfigured.
   */
  export function isUnavailable(project: WireTypes.Project): boolean {
    const status = project.sourceLastFetchHTTPCode;
    return hasSourceUrl(project) &&
      status !== undefined &&
      status >= 500 &&
      status < 600;
  }

  export function isAccessDenied(project: WireTypes.Project): boolean {
    return project.sourceLastFetchHTTPCode === 401 || project.sourceLastFetchHTTPCode === 403;
  }

  export function parseGithubURL(rawUrl: string): { error: string } | { repo: string, owner: string } {
    if (rawUrl.startsWith('git@github.com:')) {
      rawUrl = rawUrl.substring(rawUrl.indexOf(':') + 1);
      if (rawUrl.endsWith('.git'))
        rawUrl = rawUrl.slice(0, -4);
      const [owner, repo] = rawUrl.split('/');
      if (!owner || !repo)
        return { error: 'Failed to parse github URL' };
      return { owner, repo };
    }
    let url: URL;
    try {
      url = new URL(rawUrl);
    } catch (e) {
      return { error: 'failed to parse URL' };
    }
    if (url.host !== 'github.com')
      return { error: 'Only github.com projects are supported at this time' };
    let [, owner, repo] = url.pathname.split('/');
    if (!owner || !repo)
      return { error: 'Failed to parse github URL' };
    if (repo.endsWith('.git'))
      repo = repo.substring(0, repo.length - '.git'.length);
    return { owner, repo };
  }

  /**
   * Splits a GitLab project URL into the two halves we persist: `owner` carries
   * every namespace level (`group/subgroup`), `repo` is the project itself.
   */
  export function parseGitlabURL(rawUrl: string, gitlabOrigin = 'https://gitlab.com'): { error: string } | { owner: string, repo: string } {
    const configuredUrl = new URL(gitlabOrigin);
    const configuredPath = configuredUrl.pathname.replace(/\/+$/, '');
    let path: string;
    if (rawUrl.startsWith(`git@${configuredUrl.hostname}:`)) {
      path = rawUrl.substring(rawUrl.indexOf(':') + 1);
    } else {
      let url: URL;
      try {
        url = new URL(rawUrl);
      } catch (e) {
        return { error: 'failed to parse URL' };
      }
      if (url.origin !== configuredUrl.origin)
        return { error: `Only projects from ${configuredUrl.origin} are supported by this deployment` };
      path = url.pathname;
      if (configuredPath && path.startsWith(configuredPath + '/'))
        path = path.substring(configuredPath.length);
    }
    // Everything past GitLab's `/-/` route marker addresses a page inside the
    // project rather than the project itself.
    const routeMarker = path.indexOf('/-/');
    if (routeMarker !== -1)
      path = path.substring(0, routeMarker);
    if (path.endsWith('.git'))
      path = path.substring(0, path.length - '.git'.length);

    const segments = path.split('/').filter(segment => segment.length > 0);
    // A project always sits inside at least one namespace, and a project path
    // is a single segment - so the last segment is always the project.
    if (segments.length < 2)
      return { error: 'Failed to parse gitlab URL' };
    const repo = segments.pop()!;
    return { owner: segments.join('/'), repo };
  }
}
